/**
 * «SPILLESTED» I SIDEMENYEN (SPILLESTED-KONSERT, planen §6, agent A; sak 229).
 *
 * Husets venue-beholder, med navnet øverst og undermenyen under: Oversikt, Arrangementer,
 * Datoforespørsler, Saler og spesifikasjon, Personer, Ressurser, Innstillinger. Mockupens skjerm 1
 * viser den utfoldet, og det er den her: et hus har som regel ett spillested, og en undermeny man
 * må klikke fram for å se sin egen dag er et klikk for mye.
 *
 * Menyen bygges av data: seksjonen finnes bare når huset har en rad av typen `venue`. Lista er
 * den samme spørringen festivalseksjonen gjør, så den koster ingen ekstra forespørsel.
 *
 * SAK 229: INGEN ÅRGANG. Alle sju peker på spillestedets egne adresser under `/venue/$festivalId`,
 * og menyen spør ikke lenger etter årgangene. Frivillige er borte (229.5).
 */
import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_TIMEZONE,
  type FestivalDto,
  type MeResponse,
} from '@tvitvi/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { festivalsQueryOptions } from '@/api/queries/festivals';
import type { NavSectionDef } from '@/app/navigation';
import { actorFromMe, allows } from '@/auth/actor';
import { declaredModulesOf } from '@/auth/organization-modules';
import { EmptyState } from '@/components/state/EmptyState';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { ICONS } from '@/components/ui/icons';
import { FestivalFormModal } from '@/features/festivals/FestivalFormModal';
import { venueEnquirySummaryQueryOptions } from '@/features/date-requests/date-request-queries';
import { cn } from '@/lib/cn';
import type { Actor } from '@tvitvi/shared';

import { VENUE_NAV_ITEMS, venueLocationOf, type VenueNavItemDef } from './event-nav';
import { NAV_ROW, NAV_ROW_ICON, NAV_ROW_LINK, NAV_SECTION_HEADING } from './nav-styles';

const ROW = NAV_ROW;
const ROW_LINK = NAV_ROW_LINK;

export function VenueNavSection({
  section,
  me,
}: {
  section: NavSectionDef;
  me: MeResponse;
}): ReactNode {
  const actor = actorFromMe(me);
  const organizationId = me.activeOrganizationId;
  const declared = declaredModulesOf(me);
  /*
   * INGEN SPILLESTEDSMENY FOR EN GJEST (QA6 K4, M6). Det et spillested deler er en opptredende, og
   * den åpnes på programpostens egen side. Oversikt, Personer og Ressurser slapp gjesten inn
   * (`festival:read`, `crew_member:list` og `resource:list` er lesecellene en gjest har) og viste
   * tomme sider som løy om huset: «alle vakter har folk», «ingen ressurser ennå».
   */
  if (organizationId === null || actor?.orgRole === 'guest' || !allows(actor, 'festival:list')) {
    return null;
  }
  return (
    <VenueNavList
      section={section}
      organizationId={organizationId}
      actor={actor}
      hasFestivalSection={declared === undefined ? null : declared.includes('festival')}
    />
  );
}

function VenueNavList({
  section,
  organizationId,
  actor,
  hasFestivalSection,
}: {
  section: NavSectionDef;
  organizationId: string;
  actor: Actor | null;
  /**
   * Festivalseksjonen står i menyen (modulen Festival) og har «+» selv. `null` i overgangen (ingen
   * `modules` i `/v1/me`): da avgjør lista, som før — et hus uten festivaler har «+» her.
   */
  hasFestivalSection: boolean | null;
}): ReactNode {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery(festivalsQueryOptions(organizationId));
  const [createOpen, setCreateOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const here = venueLocationOf(pathname);

  const all = query.data?.items ?? [];
  const venues = all
    .filter((festival) => festival.kind === 'venue')
    .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  /*
   * Seksjonen finnes når huset har modulen Spillested (0483, `Sidebar`). Uten et spillested ennå er
   * veien ut av tomtilstanden i den — et hus som får Spillested i Control Room har ingen annen.
   */
  const mayCreate = allows(actor, 'festival:create');
  /* Et hus uten «Festivaler»-seksjon har «+» her. */
  const canCreate =
    mayCreate && !(hasFestivalSection ?? all.some((one) => one.kind === 'festival'));
  if (venues.length === 0 && !query.isSuccess) return null;
  if (venues.length === 0) {
    return (
      <div className="relative pt-3 first:pt-0" data-testid={`nav-section-${section.id}`}>
        {section.titleKey ? (
          <div className="flex min-h-7 items-center px-2">
            <h2 className={NAV_SECTION_HEADING}>{t(section.titleKey)}</h2>
          </div>
        ) : null}
        <EmptyState
          compact
          icon="venue"
          title={t('nav.venueEmpty.title')}
          description={t('nav.venueEmpty.description')}
          {...(mayCreate
            ? {
                action: (
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="venue-create-empty"
                    onClick={() => setCreateOpen(true)}
                  >
                    {t('nav.venueEmpty.create')}
                  </Button>
                ),
              }
            : {})}
        />
        {mayCreate ? (
          <FestivalFormModal
            open={createOpen}
            onOpenChange={setCreateOpen}
            organizationId={organizationId}
            defaultTimezone={DEFAULT_TIMEZONE}
            defaultCountryCode={DEFAULT_COUNTRY_CODE}
            kind="venue"
            onSaved={(festival) => {
              void navigate({ to: '/festivals/$festivalId', params: { festivalId: festival.id } });
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative pt-3 first:pt-0" data-testid={`nav-section-${section.id}`}>
      <div className="flex min-h-7 items-center justify-between gap-1 px-2">
        {section.titleKey ? <h2 className={NAV_SECTION_HEADING}>{t(section.titleKey)}</h2> : null}
        {canCreate ? (
          <IconButton
            icon="plus"
            label={t('nav.venueEmpty.create')}
            data-testid="venue-nav-create"
            onClick={() => setCreateOpen(true)}
          />
        ) : null}
      </div>
      <ul className="flex flex-col gap-px">
        {venues.map((venue) => (
          <VenueNavItem
            key={venue.id}
            venue={venue}
            organizationId={organizationId}
            actor={actor}
            within={here === venue.id}
          />
        ))}
      </ul>
      {canCreate ? (
        <FestivalFormModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          organizationId={organizationId}
          defaultTimezone={DEFAULT_TIMEZONE}
          defaultCountryCode={DEFAULT_COUNTRY_CODE}
          kind="venue"
          onSaved={(festival) => {
            void navigate({ to: '/festivals/$festivalId', params: { festivalId: festival.id } });
          }}
        />
      ) : null}
    </div>
  );
}

function VenueNavItem({
  venue,
  organizationId,
  actor,
  within,
}: {
  venue: FestivalDto;
  organizationId: string;
  actor: Actor | null;
  within: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const Icon = ICONS.venue;
  /*
   * QA16 funn 28: MERKET OG FANEN ER DEN SAMME TELLINGEN. Merket leste Oversikt sin (som ikke ble
   * oppdatert når et svar ble lagret), fanen sin egen; nå leser begge forespørslene som venter på svar
   * (`/venue-enquiries/summary`), og svaret tømmer begge.
   */
  const summary = useQuery({
    ...venueEnquirySummaryQueryOptions(organizationId, { festivalId: venue.id }),
    enabled: allows(actor, 'venue_enquiry:list'),
  });
  const items = VENUE_NAV_ITEMS.filter((item) => allows(actor, item.permission));
  const open = summary.data?.waiting ?? 0;

  return (
    <li>
      <Link
        to="/venue/$festivalId"
        params={{ festivalId: venue.id }}
        activeOptions={{ exact: true }}
        aria-current={within ? 'location' : undefined}
        data-testid={`nav-venue-${venue.slug}`}
        className={cn(ROW_LINK, ROW, 'px-2', within && 'font-semibold')}
      >
        <Icon aria-hidden className={NAV_ROW_ICON} />
        <span className="flex-1 truncate">{venue.name}</span>
      </Link>
      <ul className="flex flex-col gap-px py-px">
        {items.map((item) => (
          <li key={item.id}>
            <VenueSubLink
              item={item}
              festivalId={venue.id}
              slug={venue.slug}
              badge={
                /* Ubesvarte forespørsler bor under Saler nå, og merket følger med dit. */
                item.id === 'rooms' && open > 0 ? (open >= 100 ? '99+' : String(open)) : null
              }
              badgeLabel={t('venue.nav.openRequests', { count: open })}
            />
          </li>
        ))}
      </ul>
    </li>
  );
}

function VenueSubLink({
  item,
  festivalId,
  slug,
  badge,
  badgeLabel,
}: {
  item: VenueNavItemDef;
  festivalId: string;
  slug: string;
  badge: string | null;
  badgeLabel: string;
}): ReactNode {
  const { t } = useTranslation();
  const className = cn(ROW_LINK, ROW, 'ps-8 pe-2');
  const testId = `nav-venue-${item.id}-${slug}`;
  const label = (
    <>
      <span className="flex-1 truncate">{t(item.labelKey)}</span>
      {badge === null ? null : (
        <span
          className="numeric rounded-full bg-accent-subtle px-1.5 text-2xs text-accent-text"
          aria-label={badgeLabel}
        >
          {badge}
        </span>
      )}
    </>
  );

  switch (item.id) {
    case 'overview':
      return (
        <Link
          to="/venue/$festivalId"
          params={{ festivalId }}
          activeOptions={{ exact: true }}
          data-testid={testId}
          className={className}
        >
          {label}
        </Link>
      );
    case 'rooms':
      return (
        <Link
          to="/venue/$festivalId/rooms"
          params={{ festivalId }}
          data-testid={testId}
          className={className}
        >
          {label}
        </Link>
      );
    case 'people':
      return (
        <Link
          to="/venue/$festivalId/people"
          params={{ festivalId }}
          data-testid={testId}
          className={className}
        >
          {label}
        </Link>
      );
    case 'resources':
      return (
        <Link
          to="/venue/$festivalId/resources"
          params={{ festivalId }}
          data-testid={testId}
          className={className}
        >
          {label}
        </Link>
      );
    case 'settings':
      return (
        <Link
          to="/venue/$festivalId/settings"
          params={{ festivalId }}
          data-testid={testId}
          className={className}
        >
          {label}
        </Link>
      );
  }
}
